import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { dashboardAPI, ordersAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusLabel, getStatusColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
} from 'recharts';
import {
    Package,
    DollarSign,
    Clock,
    CheckCircle,
    Truck,
    XCircle,
    TrendingUp,
    ArrowRight,
    ShoppingBag
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, chartRes, ordersRes] = await Promise.all([
                    dashboardAPI.getStats(),
                    dashboardAPI.getChartData(),
                    ordersAPI.getAll({ limit: 5 }),
                ]);
                setStats(statsRes.data);
                setChartData(chartRes.data);
                setRecentOrders(ordersRes.data.slice(0, 5));
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-96">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    const statCards = [
        {
            title: 'Commandes Totales',
            value: stats?.total_orders || 0,
            icon: Package,
            color: 'text-primary',
            bgColor: 'bg-primary/10',
        },
        {
            title: 'Revenu Total',
            value: formatCurrency(stats?.total_revenue || 0),
            icon: DollarSign,
            color: 'text-emerald-600',
            bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
        },
        {
            title: 'En Attente',
            value: stats?.pending_orders || 0,
            icon: Clock,
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        },
        {
            title: "Aujourd'hui",
            value: stats?.orders_today || 0,
            icon: TrendingUp,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        },
    ];

    const statusCards = [
        { label: 'Confirmées', value: stats?.confirmed_orders || 0, icon: CheckCircle, color: 'text-emerald-600' },
        { label: 'Expédiées', value: stats?.shipped_orders || 0, icon: Truck, color: 'text-blue-600' },
        { label: 'Livrées', value: stats?.delivered_orders || 0, icon: ShoppingBag, color: 'text-green-600' },
        { label: 'Annulées', value: stats?.cancelled_orders || 0, icon: XCircle, color: 'text-red-600' },
    ];

    return (
        <Layout>
            <div className="space-y-8" data-testid="dashboard">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight">Tableau de Bord</h1>
                        <p className="text-muted-foreground mt-1">
                            Bienvenue sur BBM - Best Business Management
                        </p>
                    </div>
                    <Link to="/commandes">
                        <Button className="btn-luxury" data-testid="view-all-orders-btn">
                            Voir les Commandes
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {statCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <Card key={index} className="card-luxury hover-lift" data-testid={`stat-card-${index}`}>
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">{stat.title}</p>
                                            <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                                        </div>
                                        <div className={cn('w-12 h-12 rounded-[2px] flex items-center justify-center', stat.bgColor)}>
                                            <Icon className={cn('w-6 h-6', stat.color)} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Revenue Chart */}
                    <Card className="card-luxury" data-testid="revenue-chart">
                        <CardHeader>
                            <CardTitle className="text-lg">Revenus (7 derniers jours)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={chartData}>
                                        <defs>
                                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="hsl(43, 74%, 52%)" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="hsl(43, 74%, 52%)" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) => {
                                                const date = new Date(value);
                                                return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                                            }}
                                        />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '2px',
                                            }}
                                            formatter={(value) => [formatCurrency(value), 'Revenu']}
                                            labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="revenue"
                                            stroke="hsl(43, 74%, 52%)"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorRevenue)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Orders Chart */}
                    <Card className="card-luxury" data-testid="orders-chart">
                        <CardHeader>
                            <CardTitle className="text-lg">Commandes (7 derniers jours)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) => {
                                                const date = new Date(value);
                                                return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                                            }}
                                        />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '2px',
                                            }}
                                            formatter={(value) => [value, 'Commandes']}
                                            labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')}
                                        />
                                        <Bar dataKey="orders" fill="hsl(43, 74%, 52%)" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Status Overview & Recent Orders */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Status Overview */}
                    <Card className="card-luxury" data-testid="status-overview">
                        <CardHeader>
                            <CardTitle className="text-lg">Statut des Commandes</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {statusCards.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <div key={index} className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <Icon className={cn('w-5 h-5', item.color)} />
                                                <span className="text-sm">{item.label}</span>
                                            </div>
                                            <span className="font-semibold">{item.value}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Recent Orders */}
                    <Card className="card-luxury lg:col-span-2" data-testid="recent-orders">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg">Commandes Récentes</CardTitle>
                            <Link to="/commandes">
                                <Button variant="ghost" size="sm" data-testid="see-all-btn">
                                    Voir tout
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {recentOrders.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">
                                        Aucune commande pour le moment
                                    </p>
                                ) : (
                                    recentOrders.map((order) => (
                                        <div
                                            key={order.id}
                                            className="flex items-center justify-between p-4 rounded-[2px] bg-muted/30 hover:bg-muted/50 transition-colors"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="order-number font-medium">{order.n_commande}</span>
                                                    <Badge className={cn('status-badge', getStatusColor(order.status))}>
                                                        {getStatusLabel(order.status)}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground truncate mt-1">
                                                    {order.full_name} - {order.produit}
                                                </p>
                                            </div>
                                            <div className="text-right ml-4">
                                                <p className="font-semibold">{formatCurrency(order.prix_total)}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatDate(order.created_at).split(' ')[0]}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}
